// AGC: ManagementPlan - Step 2: View and edit generated management tasks
import React, { useState, useMemo } from 'react'
import { ArrowLeft, Calendar, Check } from 'lucide-react'
import { GlassCard } from '../shared'
import { generateManagementPlan, ManagementTask } from '../../data/managementPlans'
import { CropType, type Screen } from '../../types'
import { useTranslation } from '../../hooks/useTranslation'

interface ManagementPlanProps {
  plotId: string
  cropType: CropType
  cropName: string
  onBack: () => void
  onNavigate: (screen: Screen, data?: any) => void
}

/**
 * AGC: ManagementPlan - Shows generated tasks for the crop
 */
export const ManagementPlan: React.FC<ManagementPlanProps> = ({
  plotId,
  cropType,
  cropName,
  onBack,
  onNavigate,
}) => {
  const { t } = useTranslation()
  // AGC: Generate management plan based on crop
  const [tasks] = useState<ManagementTask[]>(() => generateManagementPlan(cropType, cropName))

  // AGC: Calculate dates based on today
  const tasksWithDates = useMemo(() => {
    const today = new Date()
    return tasks.map(task => {
      const date = new Date(today)
      date.setDate(date.getDate() + task.daysFromStart)
      return {
        ...task,
        date: date.toLocaleDateString('pt-BR'),
      }
    })
  }, [tasks])

  // AGC: Handle task click
  const handleTaskClick = (task: ManagementTask & { date: string }) => {
    onNavigate('lot-task-detail', {
      plotId,
      cropType,
      cropName,
      task,
    })
  }

  // AGC: Get task type icon
  const getTaskTypeIcon = (taskType: string) => {
    switch (taskType) {
      case 'preparo':
        return '🚜'
      case 'plantio':
        return '🌱'
      case 'aplicacao':
        return '💧'
      case 'colheita':
        return '🌾'
      default:
        return '📋'
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Header */}
      <header className="bg-[#0B5F4A] px-4 pt-4 pb-6 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>
        
        <h1 className="text-white text-2xl font-bold">Plano de Manejo</h1>
        <p className="text-emerald-200 text-sm mt-2">
          {cropName} - {tasks.length} tarefas geradas
        </p>
      </header>

      <div className="px-4 py-4 space-y-4">
        {/* AGC: Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            ℹ️ Plano baseado em recomendações técnicas (EMBRAPA/INTA). 
            Toque em uma tarefa para ver produtos e dosagens recomendados.
          </p>
        </div>

        {/* AGC: Task List */}
        <div className="space-y-3">
          {tasksWithDates.map((task, index) => (
            <button
              key={task.id}
              onClick={() => handleTaskClick(task)}
              className="w-full text-left"
            >
              <GlassCard hover>
                <div className="flex items-start gap-3">
                  {/* AGC: Order Number */}
                  <div className="w-8 h-8 rounded-full bg-[#0B5F4A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">{index + 1}</span>
                  </div>

                  <div className="flex-1">
                    {/* AGC: Task Name */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{getTaskTypeIcon(task.taskType)}</span>
                      <h3 className="text-base font-semibold text-gray-900">{task.name}</h3>
                    </div>

                    {/* AGC: Description */}
                    <p className="text-sm text-gray-600 mb-2">{task.description}</p>

                    {/* AGC: Date */}
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-xs text-gray-500">{task.date}</span>
                    </div>
                  </div>

                  {/* AGC: Status Badge */}
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    task.status === 'concluida'
                      ? 'bg-emerald-100 text-emerald-700'
                      : task.status === 'em_andamento'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {task.status === 'concluida' ? t('tasks.statusCompleted') :
                     task.status === 'em_andamento' ? t('tasks.statusInProgress') : t('tasks.statusPending')}
                  </div>
                </div>
              </GlassCard>
            </button>
          ))}
        </div>

        {/* AGC: Confirm Button */}
        <div className="pt-4">
          <button
            onClick={() => {
              // AGC: In real app, save tasks to plot
              console.log('AGC: Saving management plan for plot:', plotId)
              // AGC: Navigate back to lot detail
              onNavigate('plot-detail', { plotId })
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-[#0B5F4A] rounded-lg text-white font-semibold hover:bg-[#094935] shadow-lg"
          >
            <Check className="w-5 h-5" />
            Confirmar plano de manejo
          </button>
        </div>
      </div>
    </div>
  )
}

export default ManagementPlan

